/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { Checkbox, Divider, Flex, Text, TonicProps } from '@tonic-one/react';
import cloneDeep from 'lodash/cloneDeep';
import React, {
  Key,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useWhyDidYouUpdate } from 'src/utils/hooks';

import Tree from '../Tree';
import { FlexContainer, SearchInput } from './Components';
import { useCasTreeTransferContext } from './context';
import { useTreeStyle } from './style';
import { TreeDataType } from './type';
import { flatTree, searchTree, traverseTreeData } from './utils';
import { TreeNode } from '../Tree/type';

export interface TransferLeftProps extends TreeDataType, TonicProps {
  handleChange?: (value: Key[]) => void;
  handleSelectedItemsChange?: (selectedItems: TreeNode[]) => void;
  title?: string;
  placeholder?: string;
  onExpand?: Function;
  onSearch?: Function;
}

const LeftBox: React.FC<TransferLeftProps> = ({
  treeData,
  handleChange,
  handleSelectedItemsChange,
  title,
  placeholder,
  onExpand,
  onSearch,
  ...tonicProps
}) => {
  const { t } = useTranslation();
  useWhyDidYouUpdate('LeftBox', {
    treeData,
    handleChange,
    handleSelectedItemsChange,
    title,
    placeholder,
    onExpand,
    onSearch,
  });
  const {
    checkListLeft,
    handleCheckListLeftChange,
    dataSourceLeft,
    handleDataSourceLeftChange,
    flatArray,
    handleFlatArrayChange,
    handleSonParMapChange,
    finallyValue,
    handleFinallyValueChange,
    handleIsBrightRightChange,
    indeterminateLeft,
    handleIndeterminateLeftChange,
    readOnlyAllKeyLeft,
    handleReadOnlyAllKeyLeftChange,
    readOnlyTreeDataSource,
    handleReadOnlyTreeDataSourceChange,
    disabled,
    isLeftOnly,
  } = useCasTreeTransferContext();
  const [treeStyle] = useState(useTreeStyle());
  const [checkAll, setCheckAll] = useState<boolean>(false); // 控制全选
  const [searchText, setSearchText] = useState<string>(''); // 用于搜索title
  const [folderKey, setFolderKey] = useState<Key | null>(null);
  const [showCheckAll] = useState(false);
  // const [renderCount, addRenderCount] = useReducer((c) => c + 1, 0);
  // const leftTitle = typeof title === 'undefined' ? 'Available items' : title;
  const treeDataRef = React.useRef<typeof treeData>(cloneDeep(treeData));
  useEffect(() => {
    // 更新treeDataRef
    treeDataRef.current = cloneDeep(treeData);
    // 遍历树形数据设置parentId
    const sonParMap: Map<Key, Key | null> = new Map();
    traverseTreeData({
      treeData: treeDataRef.current,
      callback(item, index, parentId) {
        item.parentId = parentId;
        sonParMap.set(item.key, item.parentId);
        item.disabled = disabled === true ? disabled : item.disabled;
      },
    });
    handleSonParMapChange(sonParMap);
    // 将所以树形数据拉平，为后续组装右侧的树形树形使用
    const newFlatArray = flatTree({
      treeData: cloneDeep(treeDataRef.current),
    });
    handleFlatArrayChange(newFlatArray);
    // 得到左侧所有的key，主要用于全选
    const allKey: string[] = newFlatArray.map(({ key }) => key);
    // 保存左侧数据源所有的key
    handleReadOnlyAllKeyLeftChange(allKey);
    // 保存到左侧的数据源
    handleDataSourceLeftChange({
      treeData: cloneDeep(treeDataRef.current),
    });
    // 保存左侧树的数据源，只读，用于搜索
    handleReadOnlyTreeDataSourceChange({
      treeData: cloneDeep(treeDataRef.current),
    });
  }, [treeData, disabled]);
  // useEffect(() => {
  //   // 初始时checkListLeft为空，没有必要触发handleChange
  //   // checkListLeft第二次变化是被赋值为defaultValue，此时也没有必要触发handleChange
  //   if (renderCount <= 1) {
  //     addRenderCount();
  //     return;
  //   }
  //   // 左侧选择数据时回调change
  //   if (handleChange) {
  //     handleChange(checkListLeft);
  //   }
  // }, [checkListLeft, renderCount]);
  useEffect(() => {
    if (checkListLeft.length !== readOnlyAllKeyLeft.length) {
      setCheckAll(false);
    }
  }, [checkListLeft, readOnlyAllKeyLeft]);
  const handleCheckBoxChange = useCallback(
    (e: any) => {
      // 处理全选和半选
      handleCheckListLeftChange(e.target.checked ? readOnlyAllKeyLeft : []);
      // 处理全选状态
      setCheckAll(e.target.checked);
      // 处理半选状态
      handleIndeterminateLeftChange(false);
      // 处理to Right 高亮
      handleIsBrightRightChange(e.target.checked);
      // 触发回调
      if (handleChange) {
        handleChange(e.target.checked ? readOnlyAllKeyLeft : []);
      }
      if (handleSelectedItemsChange) {
        handleSelectedItemsChange(e.target.checked ? flatArray : []);
      }
    },
    [readOnlyAllKeyLeft, flatArray]
  );

  const handleSelectNode = useCallback(
    (keyArr: any) => {
      // 处理左侧选中的key
      handleCheckListLeftChange([...keyArr].sort());
      // 处理全选状态
      setCheckAll(readOnlyAllKeyLeft.length === keyArr.length);
      // 处理半选状态
      handleIndeterminateLeftChange(
        keyArr.length && readOnlyAllKeyLeft.length !== keyArr.length
      );
      // 处理 to Right 高亮
      handleIsBrightRightChange(!!keyArr.length);
      // 触发回调
      if (handleChange) {
        handleChange(keyArr);
      }
      if (handleSelectedItemsChange) {
        const entries = flatArray.filter((item) => keyArr.includes(item.key));
        handleSelectedItemsChange(entries);
      }
    },
    [readOnlyAllKeyLeft, flatArray]
  );

  const handleSearchTree = useCallback(
    async (value: string) => {
      const trimmedValue = value.trim();
      if (trimmedValue === '') {
        // 点击清除搜索或者手动删除文字按下回车
        const treeData = cloneDeep(readOnlyTreeDataSource.treeData);
        // 暂不处理禁用选中的数据
        // 处理左侧数据源选中数据的禁用
        // traverseTreeData({
        //   treeData,
        //   callback(item) {
        //     item['disabled'] = finallyValue.includes(item.key);
        //   },
        // });
        // 保存左侧的数据源
        console.log('handleSearchTree treeData:', treeData);
        handleDataSourceLeftChange({
          treeData,
        });
        if (onSearch) {
          await onSearch('');
        }
      } else {
        // 搜索树
        if (onSearch) {
          const result = await onSearch(trimmedValue);
          // handleDataSourceLeftChange({
          //   treeData: result,
          // });
        } else {
          handleDataSourceLeftChange({
            treeData: searchTree(trimmedValue, dataSourceLeft.treeData),
          });
        }
      }
    },
    [readOnlyTreeDataSource.treeData, dataSourceLeft.treeData, finallyValue]
  );
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchText(e.target.value);
    },
    []
  );
  const handleExpand = React.useCallback(
    async (expandKeys: Key[]) => {
      const tmpKey = expandKeys[expandKeys.length - 1];
      if (onExpand) {
        await onExpand(expandKeys);
      }
      setFolderKey(tmpKey);
    },
    [onExpand]
  );
  useEffect(() => {
    const tmpCheckListLeft = new Set<React.Key>(checkListLeft);
    flatArray.forEach((item) => {
      // add children
      if (
        item?.parentId === folderKey &&
        item.key !== 'all' &&
        item.key !== 'showMore' &&
        tmpCheckListLeft.has(item.parentId)
      ) {
        tmpCheckListLeft.add(item.key);
      }
    });
    handleCheckListLeftChange(Array.from(new Set(tmpCheckListLeft)));
  }, [flatArray]);
  useWhyDidYouUpdate('LeftBox', {
    checkListLeft,
    dataSourceLeft,
    finallyValue,
    indeterminateLeft,
    readOnlyAllKeyLeft,
    readOnlyTreeDataSource,
    disabled,
    treeData,
    handleChange,
    handleSelectedItemsChange,
    onExpand,
    placeholder,
    title,
    searchText,
  });
  return (
    <FlexContainer direction="column" width="520px" {...tonicProps}>
      {title && (
        <Flex mb="12px">
          <Text>{title}</Text>
        </Flex>
      )}
      {showCheckAll && (
        <Flex>
          <Checkbox
            onChange={handleCheckBoxChange}
            checked={checkAll}
            indeterminate={indeterminateLeft}
            disabled={disabled}
          />
          <Text ml="8px">{`${checkListLeft.length} / ${readOnlyAllKeyLeft.length} item selected`}</Text>
        </Flex>
      )}
      <Flex mb="12px" flexDirection="column">
        <SearchInput
          onChange={handleSearchChange}
          onSearch={handleSearchTree}
          value={searchText}
          placeholder={placeholder || t('policy.placeholder.policySearchItems')}
          autoClear
          width="100%"
          disabled={disabled}
        />
      </Flex>
      <Divider mb="12px" />
      <Tree
        flatArray={flatArray}
        treeData={dataSourceLeft.treeData}
        onCheck={handleSelectNode}
        checkedKeys={checkListLeft as (string | number)[]}
        style={treeStyle}
        onExpand={handleExpand}
      ></Tree>
    </FlexContainer>
  );
};

export default React.memo(LeftBox);
